import { useState, useMemo, useContext, createContext } from 'react'
import Dialog from '@mui/material/Dialog'
import useCallbackDebounced from '../../../hooks/shared/useCallbackDebounced'
import { useOverlayPageNavigateBack } from '../../../hooks/navigation/usePersistedBackgroundLocation'
import TemplateDialogPanel from '../../features/templates/TemplateDialogPanel'

const TemplateDialogContext = createContext<{
  openTemplateDialog: () => void
  closeTemplateDialog: () => void
} | null>(null)

const useTemplateDialog = () => {
  const context = useContext(TemplateDialogContext)
  if (!context) {
    throw new Error('no TemplateDialogProvider found when calling useTemplateDialog()')
  }
  return context
}

const TemplateDialogProvider = ({ children }) => {
  const overlayPageNavigateBack = useOverlayPageNavigateBack()
  const [open, setOpen] = useState(false)
  const openTemplateDialog = useCallbackDebounced(() => {
    setOpen(true)
  }, [])
  const closeTemplateDialog = useCallbackDebounced(() => {
    setOpen(false)
  }, [])
  const onClose = useCallbackDebounced(() => {
    overlayPageNavigateBack()
  }, [overlayPageNavigateBack])
  const onSave = useCallbackDebounced(() => {
    overlayPageNavigateBack()
  }, [overlayPageNavigateBack])
  const value = useMemo(
    () => ({
      openTemplateDialog,
      closeTemplateDialog,
    }),
    [closeTemplateDialog, openTemplateDialog],
  )
  return (
    <TemplateDialogContext.Provider value={value}>
      {children}
      <Dialog
        fullScreen
        open={open}
        disableEscapeKeyDown
        onClose={onClose}
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: 'pageBackground',
          },
          zIndex: 20,
        }}
      >
        <TemplateDialogPanel onSave={onSave} onClose={onClose} />
      </Dialog>
    </TemplateDialogContext.Provider>
  )
}

export { useTemplateDialog, TemplateDialogProvider }
export default TemplateDialogProvider
